import React from 'react';

const SITE_URL = 'https://coachginge.com';
const PLAYLIST_URL = 'https://www.youtube.com/playlist?list=PLV89ucBocjHs';

const post = {
  slug: 'coach-ginge-for-kids',
  title: 'Coach Ginge goes back to school',
  date: '2026-09-30',
  cover: '/blog/coach-ginge-for-kids/cover.jpg',
  coverAlt:
    'Coach Ginge, a ginger cat with a whistle, with Sugar and Fluffy, two white cats, beside the words Move with the cats',
  excerpt:
    'Coach Ginge has a new class: three free five-minute brain breaks for children, and a home of his own at coachginge.com.',
  content: (
    <>
      <p>
        Six months ago we said goodbye to Grandad, our ginger-and-white Persian,
        and turned him into Coach Ginge, an AI-animated cat who leads short
        workouts on YouTube. <a href="/blog/coach-ginge">That story is here</a>.
        This is the next chapter. Coach Ginge has a new class, and it is a lot
        smaller and a lot louder.
      </p>

      <h2>A better class for a cat like Grandad</h2>
      <p>
        The grown-up workouts were fun to make, but they never found many
        people. Watching them back, we kept noticing that Coach Ginge made more
        sense to someone else. A fluffy ginger cat with a whistle, shouting
        JUMP and DUCK, is not really a gym instructor. He is someone children
        would follow round a room.
      </p>
      <p>
        Grandad would have approved. His whole approach to life was to get
        involved in whatever was happening, then sit down right in the middle
        of it. That is more or less what a classroom brain break is: everyone
        stands up, joins in for a few minutes, and sits back down ready to
        work.
      </p>

      <h2>Three brain breaks</h2>
      <p>
        So we made three. Each one is about five minutes long, done standing
        up, with no equipment. Coach Ginge calls out every move just before it
        comes, so nobody has to learn anything first. Two fluffy white friends,
        Sugar and Fluffy, run alongside him.
      </p>
      <ul>
        <li>
          <strong>Laser Run.</strong> The laser dot has escaped from Coach
          Ginge&apos;s toy box, and it has to be caught before bedtime. Jump,
          duck, dodge, strike a pose, and freeze when the dino turns round.
        </li>
        <li>
          <strong>Rooftop Rundown.</strong> The dot has bounced up onto the city
          rooftops. Climbing, balancing on one leg, and freezing when the
          bulldog wakes up.
        </li>
        <li>
          <strong>Dance Party.</strong> The funfair&apos;s lights have gone out,
          and every dance move lights them up again. Four songs, a freeze dance
          and a calm stretch to finish.
        </li>
      </ul>
      <p>
        Each one ends with the Golden Paw, the prize for never moving a whisker
        at FREEZE.
      </p>

      <div className="video-embed">
        <iframe
          src="https://www.youtube.com/embed/MmbJM5ZHXFk"
          title="Laser Run, a brain break for kids with Coach Ginge"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

      <div className="post-photo-grid">
        <img
          src="/blog/coach-ginge/20250201-000002.jpg"
          alt="A selfie with Grandad looking thoroughly unimpressed"
        />
        <img
          src="/blog/coach-ginge-for-kids/coach-ginge.jpg"
          alt="The animated Coach Ginge, a ginger-and-white cat with a whistle, waving"
        />
      </div>

      <h2>How they are made</h2>
      <p>
        Like the workouts, Coach Ginge is made with the help of AI tools. Each
        episode is really a small game that plays itself: a list of levels,
        moves and lines, timed so that every move lands on the music. That
        makes it easy to keep the rules the same from one episode to the next,
        which matters when a room full of six-year-olds is relying on you to
        say FREEZE at the right moment.
      </p>
      <p>
        The videos are marked made for kids on YouTube, which means no comments
        and no targeted ads, and the channel is not monetised. They are all in
        one <a href={PLAYLIST_URL}>Brain Breaks for Kids playlist</a>.
      </p>

      <h2>A home of his own</h2>
      <p>
        This week Coach Ginge also got his own website.{' '}
        <a href={SITE_URL}>coachginge.com</a> has all three brain breaks in one
        place, plus two things for teachers: a one-page teacher card with the
        moves and a QR code for each video, and a printable Golden Paw award
        for the class. Both are free to download, with nothing to sign up to.
      </p>
      <p>
        The site sets no cookies and collects nothing, and the videos only load
        from YouTube when you press play. It felt like the right way to build
        something for children.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/coach-ginge-for-kids/coachginge-com.jpg"
          alt="The coachginge.com home page: Move with the cats, the three cats, and the three brain breaks"
        />
        <figcaption>coachginge.com, Coach Ginge&apos;s new home.</figcaption>
      </figure>

      <h2>What comes next</h2>
      <p>
        We have started telling teachers about him: a free listing on Teachers
        Pay Teachers, a Pinterest board, a few home education groups and some
        emails to schools near us. A Halloween episode is on the way.
      </p>

      <div className="post-callout">
        <h3>Know a teacher, or a small person who needs to move?</h3>
        <p>
          Send them to <a href={SITE_URL}>coachginge.com</a>. Five minutes,
          standing up, and a cat who calls every move before it comes.
        </p>
      </div>

      <p className="post-signoff">
        For Grandad, who never missed a session, and is still turning up for
        every one.
      </p>
    </>
  ),
};

export default post;
